import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { get, post, useAuth } from '../../api';
import TerrainMapPreview from '../../components/TerrainMapPreview/TerrainMapPreview';
import YieldMissionModal from '../../components/YieldMissionModal/YieldMissionModal';
import {
  DIFFICULTY_LABEL,
  MissionDetail,
  MissionGame,
} from '../../types/mission';

const PLAYER_COLORS = ['#68b8e8', '#d43318', '#f7b728', '#5db854'];

const DIFFICULTY_COLOR: Record<string, string> = {
  'very-easy': '#5db854',
  easy: '#8bc34a',
  medium: '#f7b728',
  hard: '#e8743b',
  'very-hard': '#d43318',
};

const FACTION_LABEL: Record<string, string> = {
  'blue-crown': 'Blue Crown',
  'red-horde': 'Red Horde',
  'yellow-league': 'Yellow League',
  'green-compact': 'Green Compact',
};

function gameStatus(
  game: MissionGame,
  userId: string | undefined
): { label: string; color: string } {
  if (!game.active) {
    if (!game.wonBy || game.wonBy.length === 0) {
      return { label: 'Draw', color: '#888' };
    }
    const myWonSlotIds = game.slots.filter(
      (s) => s.user?.id === userId && game.wonBy.includes(s.slotId)
    );
    return myWonSlotIds.length > 0
      ? { label: 'Won', color: '#23a047' }
      : { label: 'Lost', color: '#e53935' };
  }
  return { label: 'In progress', color: '#3298dc' };
}

export default function CampaignMissionPage() {
  const { id } = useParams();
  const [mission, setMission] = useState<MissionDetail | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [confirmYieldGameId, setConfirmYieldGameId] = useState<number | null>(
    null
  );
  const [yielding, setYielding] = useState(false);
  const navigate = useNavigate();
  const auth = useAuth();
  const userId = auth?.sub;

  const loadMission = () => {
    if (!id) return;
    get(`missions/${id}`)
      .then(setMission)
      .catch(() => {});
  };

  useEffect(() => {
    loadMission();
  }, [id]);

  const handleStart = async () => {
    setStartError(null);
    try {
      const result = await post(`missions/${id}/start`, {});
      navigate(`/games/${result.id}?from=campaign`);
    } catch {
      setStartError('Could not start the mission. Please try again.');
    }
  };

  const handleYield = async (gameId: number) => {
    setYielding(true);
    try {
      await post(`games/${gameId}/surrender`, {});
      loadMission();
    } catch {
    } finally {
      setYielding(false);
      setConfirmYieldGameId(null);
    }
  };

  if (!mission) {
    return <div>Loading...</div>;
  }

  const activeGames = mission.games.filter((g) => g.active);
  const completedGames = mission.games.filter((g) => !g.active);

  return (
    <div>
      <div className="is-flex is-align-items-center mb-4" style={{ gap: 12 }}>
        <Link to="/campaign" className="button is-small is-light">
          Back
        </Link>
        <h1 className="title is-4 mb-0">{mission.name}</h1>
      </div>

      <div className="box">
        <div
          className="is-flex"
          style={{ gap: 8, flexWrap: 'wrap', marginBottom: 14 }}
        >
          <span
            className="tag is-small"
            style={{
              backgroundColor: DIFFICULTY_COLOR[mission.difficulty],
              color: '#1a1a26',
              fontWeight: 600,
            }}
          >
            {DIFFICULTY_LABEL[mission.difficulty]}
          </span>
          <span className="tag is-small is-light">
            Act {mission.act}, mission {mission.orderInAct}
          </span>
          {mission.kind === 'finale' && (
            <span className="tag is-small is-warning is-light">Finale</span>
          )}
          <span className="tag is-small is-light">
            {mission.fogOfWar ? 'Fog of war' : 'No fog'}
          </span>
          {mission.parTurns !== undefined && (
            <span className="tag is-small is-light">
              Par {mission.parTurns} turns
            </span>
          )}
        </div>

        {mission.briefing && (
          <p
            style={{
              fontSize: '1rem',
              lineHeight: 1.6,
              color: 'var(--text-secondary)',
              marginBottom: 20,
            }}
          >
            {mission.briefing.situation}
          </p>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 20,
            marginBottom: 20,
          }}
        >
          {mission.briefing && mission.briefing.objectives.length > 0 && (
            <div>
              <h2 className="title is-6 mb-2">Objectives</h2>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {mission.briefing.objectives.map((line) => (
                  <li
                    key={line}
                    style={{
                      display: 'flex',
                      gap: 8,
                      marginBottom: 6,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    <span style={{ color: '#f7b728' }}>◆</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {mission.briefing && mission.briefing.teaches.length > 0 && (
            <div>
              <h2 className="title is-6 mb-2">What this teaches</h2>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {mission.briefing.teaches.map((line) => (
                  <li
                    key={line}
                    style={{
                      display: 'flex',
                      gap: 8,
                      marginBottom: 6,
                      color: 'var(--text-muted)',
                      fontSize: '0.9rem',
                    }}
                  >
                    <span>·</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div
          style={{
            border: '1px solid rgba(68,68,102,0.5)',
            borderRadius: 8,
            padding: '14px 16px',
            marginBottom: 20,
          }}
        >
          <div
            className="is-flex is-align-items-baseline"
            style={{ gap: 8, flexWrap: 'wrap', marginBottom: 6 }}
          >
            <strong>{mission.commander.name}</strong>
            <span style={{ color: 'var(--text-muted)' }}>
              {mission.commander.title}
            </span>
            <span
              className="tag is-small is-light"
              style={{ marginLeft: 'auto' }}
            >
              {FACTION_LABEL[mission.commander.faction] ??
                mission.commander.faction}
            </span>
          </div>
          {mission.commander.flavor && (
            <p
              style={{
                fontStyle: 'italic',
                color: 'var(--text-muted)',
                marginBottom: 10,
              }}
            >
              "{mission.commander.flavor}"
            </p>
          )}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {mission.commander.description.map((line) => (
              <li
                key={line}
                style={{
                  display: 'flex',
                  gap: 8,
                  marginBottom: 4,
                  fontSize: '0.85rem',
                  color: 'var(--text-secondary)',
                }}
              >
                <span style={{ color: '#5db854' }}>+</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <div style={{ margin: '1rem 0' }}>
          <TerrainMapPreview terrainMap={mission.terrainMap} />
        </div>

        {mission.locked ? (
          <p className="has-text-danger mb-4">
            This mission is locked. Complete prerequisite missions to unlock it.
          </p>
        ) : (
          <>
            <button className="button is-primary mb-4" onClick={handleStart}>
              Start New Game
            </button>
            {startError && <p className="has-text-danger mb-4">{startError}</p>}
          </>
        )}
      </div>

      {mission.games.length > 0 && (
        <div className="mt-5">
          <h2 className="title is-5">Games</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: 16,
            }}
          >
            {activeGames.map((game) => {
              const previewTerrain =
                game.preview?.terrainMap ?? mission.terrainMap;

              return (
                <div
                  key={game.id}
                  className="box"
                  style={{
                    borderRadius: 8,
                    padding: '14px 14px 12px',
                    borderLeft: '4px solid #3298dc',
                  }}
                >
                  <div className="is-flex is-justify-content-space-between is-align-items-center mb-2">
                    <span
                      className="tag is-info is-small"
                      style={{ border: 'none' }}
                    >
                      Active
                    </span>
                    <span
                      style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}
                    >
                      Turn {game.currentTurn + 1}
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px 12px',
                      marginBottom: 10,
                    }}
                  >
                    {game.slots.map((slot) => {
                      const color =
                        PLAYER_COLORS[slot.slotId % PLAYER_COLORS.length];
                      const isMe = slot.user?.id === userId;
                      const label = slot.user?.username ?? 'AI';
                      return (
                        <div
                          key={slot.slotId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5,
                            fontSize: '0.8rem',
                          }}
                        >
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: color,
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              color: isMe
                                ? 'var(--text-primary)'
                                : 'var(--text-muted)',
                              fontWeight: isMe ? 600 : 400,
                            }}
                          >
                            {label}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {game.preview ? (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginBottom: 10,
                      }}
                    >
                      <TerrainMapPreview
                        terrainMap={game.preview.terrainMap}
                        cellSize={4}
                        ownerOverrides={game.preview.ownerOverrides}
                        highlightOwners={false}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginBottom: 10,
                      }}
                    >
                      <TerrainMapPreview
                        terrainMap={previewTerrain}
                        cellSize={4}
                        highlightOwners={false}
                      />
                    </div>
                  )}

                  <div className="is-flex" style={{ gap: 6 }}>
                    <button
                      className="button is-small is-danger is-light"
                      onClick={() => setConfirmYieldGameId(game.id)}
                    >
                      Yield
                    </button>
                    <Link
                      className="button is-small is-primary"
                      to={`/games/${game.id}?from=campaign`}
                    >
                      Resume
                    </Link>
                  </div>
                </div>
              );
            })}

            {completedGames.map((game) => {
              const status = gameStatus(game, userId);

              return (
                <div
                  key={game.id}
                  className="box"
                  style={{
                    borderRadius: 8,
                    padding: '14px 14px 12px',
                    borderLeft: `4px solid ${status.color}`,
                  }}
                >
                  <div className="is-flex is-justify-content-space-between is-align-items-center mb-2">
                    <span
                      className="tag is-small"
                      style={{
                        backgroundColor: status.color,
                        color: '#fff',
                        border: 'none',
                      }}
                    >
                      {status.label}
                    </span>
                    <span
                      style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}
                    >
                      Turn {game.currentTurn + 1}
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px 12px',
                      marginBottom: 10,
                    }}
                  >
                    {game.slots.map((slot) => {
                      const color =
                        PLAYER_COLORS[slot.slotId % PLAYER_COLORS.length];
                      const isMe = slot.user?.id === userId;
                      const label = slot.user?.username ?? 'AI';
                      const isWinner = game.wonBy.includes(slot.slotId);
                      return (
                        <div
                          key={slot.slotId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5,
                            fontSize: '0.8rem',
                          }}
                        >
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: color,
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              color: isMe
                                ? 'var(--text-primary)'
                                : 'var(--text-muted)',
                              fontWeight: isMe ? 600 : 400,
                            }}
                          >
                            {label}{' '}
                            {isWinner && (
                              <span
                                style={{ fontSize: '0.7rem', color: '#23a047' }}
                              >
                                ★︎
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <Link
                    className="button is-small is-light"
                    to={`/games/${game.id}?spectate=true&from=campaign`}
                  >
                    Replay
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <YieldMissionModal
        gameId={confirmYieldGameId}
        yielding={yielding}
        onCancel={() => setConfirmYieldGameId(null)}
        onConfirm={handleYield}
      />
    </div>
  );
}
